import type { FoodScanItemView } from '@steado/admin-contract/user-detail';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { BadgeCell, ChipsCell, TextCell } from '@/components/table/cells';
import type { ToneName } from '@/components/table/tone';
import { useAuth } from '@/lib/auth/auth-context';

import { TabError } from './TabError';
import { formatDateTime, humanise } from '@/lib/format';
import { SCANS_PAGE_SIZE, userScansQueryOptions } from './user-detail-query';

const COLUMNS: readonly TableColumn[] = [
  { key: 'when', label: 'When', width: '0.85fr' },
  { key: 'dish', label: 'Identified dishes', width: '1.5fr' },
  { key: 'status', label: 'Status', width: '0.95fr' },
  { key: 'tier', label: 'Resolved by', width: '1.2fr' },
  { key: 'coverage', label: 'Coverage', width: '0.85fr', align: 'right' },
  { key: 'unresolved', label: 'Unresolved', width: '1.05fr' },
];

const STATUS_TONES: Readonly<Record<string, ToneName>> = {
  confirmed: 'ok',
  completed: 'info',
  pending: 'warn',
  failed: 'err',
};

interface ScansTabProps {
  readonly userId: string;
  readonly cursor: string | undefined;
  readonly onCursorChange: (cursor: string | undefined) => void;
}

export function ScansTab({ userId, cursor, onCursorChange }: ScansTabProps) {
  const { api } = useAuth();
  const query = useQuery(userScansQueryOptions(api, userId, cursor, true));

  // The API hands back only a forward cursor, so walking back needs the ones
  // already visited. Same back-stack as the users list.
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  const onNext = useCallback(() => {
    const next = query.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, cursor]);
    onCursorChange(next);
  }, [query.data, cursor, onCursorChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onCursorChange(stack[stack.length - 1]);
      return stack.slice(0, -1);
    });
  }, [onCursorChange]);

  const rows: readonly TableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((scan) => ({
        id: scan.id,
        cells: [
          <TextCell key="when" value={formatDateTime(scan.createdAt)} mono />,
          <ChipsCell key="dish" items={scan.items.map((item) => item.dishName)} max={3} />,
          <BadgeCell
            key="status"
            label={scan.status}
            tone={STATUS_TONES[scan.status] ?? 'mute'}
            dot={scan.loggedAsMeal}
          />,
          <TextCell
            key="tier"
            value={tiersOf(scan.items)}
            sub={
              scan.failureCode?.replaceAll('_', ' ') ??
              (scan.loggedAsMeal ? 'logged as a meal' : 'not acted on')
            }
          />,
          <TextCell
            key="coverage"
            // The weakest item on the plate, not the average: one dish that lost
            // a third of its weight is the thing worth looking at, and averaging
            // it against three clean catalog matches hides exactly that.
            // Only decomposition items measure coverage; a null is an absence,
            // not a zero, and 0% would read as a total failure to resolve.
            value={percentOrNull(worstCoverage(scan.items))}
            align="right"
            mono
          />,
          <ChipsCell key="unresolved" items={unresolvedAcross(scan.items)} max={2} />,
        ],
      })),
    [query.data],
  );

  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const pageOffset = cursorStack.length * SCANS_PAGE_SIZE;

  return (
    <DataTable
      caption="Food scans"
      title="Food scans"
      columns={COLUMNS}
      rows={rows}
      isLoading={query.isPending}
      emptyTitle="No scans"
      emptyBody="This user has not photographed a meal. Scans appear here whether or not they were logged."
      footer={
        <PaginationFooter
          rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
          rangeEnd={pageOffset + rows.length}
          hasPrevious={cursorStack.length > 0}
          hasNext={query.data?.pagination.hasMore ?? false}
          onPrevious={onPrevious}
          onNext={onNext}
          isLoading={query.isPending}
        />
      }
    />
  );
}

/**
 * How the plate was resolved, across all of its dishes. A thali usually mixes
 * tiers — three catalog hits and one decomposition — and naming both is more
 * informative than naming whichever dish happened to be listed first.
 */
function tiersOf(items: readonly FoodScanItemView[]): string | null {
  const tiers = [...new Set(items.flatMap((item) => (item.matchTier === null ? [] : [item.matchTier])))];
  return tiers.length === 0 ? null : tiers.map(humanise).join(' + ');
}

/** The lowest coverage any dish on the plate recorded, or null if none did. */
function worstCoverage(items: readonly FoodScanItemView[]): number | null {
  const measured = items.flatMap((item) =>
    item.ingredientCoverage === null ? [] : [item.ingredientCoverage],
  );
  return measured.length === 0 ? null : Math.min(...measured);
}

function unresolvedAcross(items: readonly FoodScanItemView[]): string[] {
  return [...new Set(items.flatMap((item) => item.unresolvedIngredients))];
}

function percentOrNull(value: number | null): string | null {
  return value === null ? null : `${String(Math.round(value * 100))}%`;
}
